import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import type {
  PlatformTeamsAppSettingsDto,
  TeamsSetupInfoDto,
  TestPlatformTeamsAppCredentialsResult,
} from '../types';

const queryKey = ['platform-teams-app'] as const;

function getErrorMessage(error: unknown): string {
  const apiError = error as {
    response?: { data?: { error?: string; detail?: string; title?: string; message?: string } };
    message?: string;
  };
  return (
    apiError.response?.data?.error
    ?? apiError.response?.data?.detail
    ?? apiError.response?.data?.title
    ?? apiError.response?.data?.message
    ?? apiError.message
    ?? 'An unexpected error occurred.'
  );
}

export function IntegrationsTeamsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => apiClient.getPlatformTeamsApp(),
  });

  // Pre-populated environment URLs sourced from the API (the same values the
  // generated PowerShell script gets baked into it). Used to auto-fill the
  // configuration form so operators don't have to retype them.
  const { data: setupInfo } = useQuery({
    queryKey: ['platform-teams-setup-info'] as const,
    queryFn: () => apiClient.getPlatformTeamsSetupInfo(),
  });

  const [form, setForm] = useState({
    clientId: '',
    operatorTenantId: '',
    microsoftAppId: '',
    botEndpoint: '',
    redirectUri: '',
    isEnabled: true,
  });
  const [secretInput, setSecretInput] = useState('');
  const [formMessage, setFormMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [secretMessage, setSecretMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [testResult, setTestResult] = useState<TestPlatformTeamsAppCredentialsResult | null>(null);
  const [mirrorAppId, setMirrorAppId] = useState(true);

  // Sync external query data into the form when it changes (covers initial load).
  const [hydratedFromId, setHydratedFromId] = useState<string | null>(null);
  if (data && hydratedFromId !== (data.updatedAt ?? '__nodata__')) {
    setHydratedFromId(data.updatedAt ?? '__nodata__');
    setForm({
      clientId: data.clientId ?? '',
      operatorTenantId: data.operatorTenantId ?? '',
      microsoftAppId: data.microsoftAppId ?? '',
      botEndpoint: data.botEndpoint ?? '',
      redirectUri: data.redirectUri ?? '',
      isEnabled: data.isEnabled,
    });
    // If existing record stored differing ClientId/MicrosoftAppId, treat that
    // as an intentional override and disable the mirror by default.
    if ((data.clientId ?? '') !== (data.microsoftAppId ?? '') && data.microsoftAppId) {
      setMirrorAppId(false);
    }
  }

  // When setup info loads, fill any blank URL fields with the env defaults
  // (works both for "new record" and "existing record but operator never set
  // these URLs" — e.g. if the bot endpoint was left blank during the initial
  // save and is now causing IsBotConfigured to be false).
  const [hydratedFromSetupInfo, setHydratedFromSetupInfo] = useState(false);
  if (setupInfo && !hydratedFromSetupInfo) {
    setHydratedFromSetupInfo(true);
    setForm((prev) => ({
      ...prev,
      botEndpoint: prev.botEndpoint || setupInfo.botEndpoint,
      redirectUri: prev.redirectUri || setupInfo.redirectUri,
    }));
  }

  const upsertMutation = useMutation({
    mutationFn: () =>
      apiClient.upsertPlatformTeamsApp({
        clientId: form.clientId.trim(),
        operatorTenantId: form.operatorTenantId.trim() || null,
        microsoftAppId: form.microsoftAppId.trim(),
        botEndpoint: form.botEndpoint.trim() || null,
        redirectUri: form.redirectUri.trim(),
        isEnabled: form.isEnabled,
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
      setFormMessage({ kind: 'success', text: 'Platform Teams configuration saved.' });
      setTimeout(() => setFormMessage(null), 4000);
    },
    onError: (err) => setFormMessage({ kind: 'error', text: getErrorMessage(err) }),
  });

  const setSecretMutation = useMutation({
    mutationFn: () => apiClient.setPlatformTeamsAppSecret({ clientSecret: secretInput }),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
      setSecretInput('');
      setSecretMessage({
        kind: 'success',
        text: 'Client secret stored in Key Vault. The platform now uses the new value.',
      });
      setTimeout(() => setSecretMessage(null), 4000);
    },
    onError: (err) => setSecretMessage({ kind: 'error', text: getErrorMessage(err) }),
  });

  const clearSecretMutation = useMutation({
    mutationFn: () => apiClient.clearPlatformTeamsAppSecret(),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKey, updated);
      setSecretMessage({
        kind: 'success',
        text: 'Stored secret URI was cleared. The Key Vault secret itself was NOT deleted.',
      });
      setTimeout(() => setSecretMessage(null), 5000);
    },
    onError: (err) => setSecretMessage({ kind: 'error', text: getErrorMessage(err) }),
  });

  const testMutation = useMutation({
    mutationFn: () => apiClient.testPlatformTeamsAppCredentials(),
    onSuccess: (result) => setTestResult(result),
    onError: (err) => setTestResult({ success: false, errorDescription: getErrorMessage(err) }),
  });

  // Render-time state adjustment: clear any stale test result when the user
  // edits a credential field, without triggering a separate render via effect.
  const formSignature = `${form.clientId}|${form.microsoftAppId}|${form.redirectUri}`;
  const [lastFormSignature, setLastFormSignature] = useState(formSignature);
  if (lastFormSignature !== formSignature) {
    setLastFormSignature(formSignature);
    if (testResult !== null) {
      setTestResult(null);
    }
  }

  // Setup guide expand/collapse — declared before any early returns so the
  // hook order stays stable. Auto-expand when the integration isn't yet ready.
  const [showGuide, setShowGuide] = useState(true);
  const isReady = data?.isReady ?? false;
  const [lastReadyState, setLastReadyState] = useState(isReady);
  if (lastReadyState !== isReady) {
    setLastReadyState(isReady);
    setShowGuide(!isReady);
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-gray-600 dark:text-gray-400">Loading platform Teams configuration…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="rounded border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200">
          <h2 className="font-semibold">Couldn't load platform Teams settings</h2>
          <p className="text-sm">{getErrorMessage(error)}</p>
        </div>
      </div>
    );
  }

  const status: PlatformTeamsAppSettingsDto | undefined = data;

  return (
    <div className="max-w-4xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">Teams Integration — Cloudium Enterprise App</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          This is the central Microsoft Entra ID application that every customer
          tenant using <em>“Cloudium app”</em> mode connects to. Operators manage
          it here. Tenants who prefer their own app can configure that on their
          own MSP Portal under Settings → Teams Integration.
        </p>
      </header>

      <ScriptSetupGuide
        expanded={showGuide}
        onToggle={() => setShowGuide((v) => !v)}
        setupInfo={setupInfo}
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-medium">Status</h2>

        {/* Detailed per-field breakdown so operators see WHICH item is missing,
            not just a single boolean. OAuth-essential fields are first; the
            bot-messaging fields are clearly labeled as separate. */}
        <div className="space-y-3">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              OAuth (required for tenants to connect their Teams)
            </p>
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <StatusItem label="Client ID" ok={!!status?.clientId} />
              <StatusItem label="Operator tenant ID" ok={!!status?.operatorTenantId} />
              <StatusItem label="OAuth redirect URI" ok={!!status?.redirectUri} />
              <StatusItem label="Client secret stored" ok={status?.clientSecretConfigured ?? false} />
              <StatusItem label="App enabled" ok={status?.isEnabled ?? false} />
            </dl>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Bot messaging (optional — only needed if Teams should be able to
              send inbound messages to Cloudium)
            </p>
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <StatusItem label="Microsoft App ID" ok={!!status?.microsoftAppId} />
              <StatusItem label="Bot endpoint URL" ok={!!status?.botEndpoint} />
            </dl>
          </div>

          <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <StatusItem label="Ready for OAuth" ok={status?.isReady ?? false} />
              <StatusItem label="Bot messaging configured" ok={status?.isBotConfigured ?? false} />
            </dl>
          </div>
        </div>

        {/* "What's missing" hint banner — only shown when at least one field
            is blank. Helps operators figure out WHY OAuth isn't ready. */}
        {status?.missingFields && status.missingFields.length > 0 && (
          <div className="mt-4 rounded border border-yellow-300 bg-yellow-50 p-3 text-sm dark:border-yellow-700 dark:bg-yellow-900/20">
            <p className="font-semibold text-yellow-900 dark:text-yellow-100">
              {status.isReady ? 'Optional fields missing' : 'Configuration incomplete'}
            </p>
            <p className="mt-1 text-yellow-800 dark:text-yellow-200">
              {status.isReady
                ? 'OAuth will work, but the following fields are still blank:'
                : 'The following fields must be set before tenants can connect their Teams:'}
            </p>
            <ul className="ml-5 mt-2 list-disc space-y-1 text-yellow-900 dark:text-yellow-100">
              {status.missingFields.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>
          </div>
        )}

        {status?.clientSecretRotatedAt && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Client secret last rotated: {new Date(status.clientSecretRotatedAt).toLocaleString()}
          </p>
        )}
        {status?.updatedAt && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Configuration last updated: {new Date(status.updatedAt).toLocaleString()}
            {status.updatedBy ? ` by ${status.updatedBy}` : ''}
          </p>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-medium">App registration details</h2>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          Paste the <strong>Application (client) ID</strong> and the freshly-generated
          <strong> Client Secret</strong> that the setup script printed. The URL fields
          below are pre-filled with the values that the script registered on the
          Microsoft Entra app — leave them as-is unless you used a custom setup.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <LabeledField label="Client ID (Application ID)" required>
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900"
              value={form.clientId}
              onChange={(e) => {
                const next = e.target.value;
                setForm((prev) => ({
                  ...prev,
                  clientId: next,
                  microsoftAppId: mirrorAppId ? next : prev.microsoftAppId,
                }));
              }}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
          </LabeledField>
          <LabeledField label="Operator tenant ID" required>
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900"
              value={form.operatorTenantId}
              onChange={(e) => setForm({ ...form, operatorTenantId: e.target.value })}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Entra tenant where this app registration was created (Cloudium's
              own tenant). The setup script prints it on the{' '}
              <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">Operator tenant ID</code>{' '}
              line. Required for the <em>Test Credentials</em> button.
            </p>
          </LabeledField>
          <div className="sm:col-span-2 rounded border border-blue-200 bg-blue-50 p-3 text-sm dark:border-blue-700 dark:bg-blue-900/20">
            <p className="font-semibold text-blue-900 dark:text-blue-100">
              About the Bot Service fields below (optional)
            </p>
            <p className="mt-1 text-blue-900 dark:text-blue-100">
              The <strong>Microsoft App ID</strong> and{' '}
              <strong>Bot endpoint URL</strong> are only needed if you want
              Teams to be able to <strong>send messages back to CloudIncidents</strong>{' '}
              — for example, when a user replies to an incident card or
              @mentions the bot in a channel. They power the Azure{' '}
              <em>Bot Framework messaging endpoint</em>.
            </p>
            <p className="mt-2 text-blue-900 dark:text-blue-100">
              <strong>You can skip them for now</strong> if you only need
              CloudIncidents to <em>post</em> messages to Teams via Microsoft
              Graph (the OAuth flow you set up above). The "Ready for OAuth"
              check above does not require them.
            </p>
            <p className="mt-2 text-blue-900 dark:text-blue-100">
              If you do want inbound bot messages, leave the recommended
              "Use the same value as Client ID" checkbox on — it reuses your
              app registration as the bot's identity, and the bot endpoint
              URL is already pre-filled with the correct API host.
            </p>
          </div>
          <LabeledField label="Microsoft App ID (Bot Service)">
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900 disabled:bg-gray-50 disabled:text-gray-500 dark:disabled:bg-gray-900/40"
              value={form.microsoftAppId}
              disabled={mirrorAppId}
              onChange={(e) => setForm({ ...form, microsoftAppId: e.target.value })}
              placeholder="00000000-0000-0000-0000-000000000000"
            />
            <label className="mt-1 flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={mirrorAppId}
                onChange={(e) => {
                  const next = e.target.checked;
                  setMirrorAppId(next);
                  if (next) {
                    setForm((prev) => ({ ...prev, microsoftAppId: prev.clientId }));
                  }
                }}
              />
              Use the same value as Client ID (recommended)
            </label>
          </LabeledField>
          <LabeledField label="Bot endpoint URL">
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900"
              value={form.botEndpoint}
              onChange={(e) => setForm({ ...form, botEndpoint: e.target.value })}
              placeholder={setupInfo?.botEndpoint ?? 'https://api.cloudincidents.se/api/teams/messages'}
            />
          </LabeledField>
          <LabeledField label="OAuth redirect URI" required>
            <input
              type="text"
              className="w-full rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900"
              value={form.redirectUri}
              onChange={(e) => setForm({ ...form, redirectUri: e.target.value })}
              placeholder={setupInfo?.redirectUri ?? 'https://api.cloudincidents.se/api/teams/connection/oauth/callback'}
            />
          </LabeledField>
        </div>
        <label className="mt-4 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isEnabled}
            onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
          />
          Enable the Cloudium Enterprise App for all tenants (disable to force BYO mode globally).
        </label>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={upsertMutation.isPending}
            onClick={() => upsertMutation.mutate()}
            className="rounded bg-primary-700 px-4 py-2 text-white hover:bg-primary-800 disabled:opacity-50"
          >
            {upsertMutation.isPending ? 'Saving…' : 'Save configuration'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending || !(status?.clientSecretConfigured && status?.clientId)}
            onClick={() => testMutation.mutate()}
            className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"
            title={status?.clientSecretConfigured && status?.clientId ? 'Round-trip a client_credentials request to validate the secret.' : 'Save a Client ID and store the client secret first.'}
          >
            {testMutation.isPending ? 'Testing…' : 'Test credentials'}
          </button>
        </div>
        {formMessage && (
          <p
            className={`mt-3 text-sm ${formMessage.kind === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}
          >
            {formMessage.text}
          </p>
        )}
        {testResult && (
          <div
            className={`mt-3 rounded border p-3 text-sm ${
              testResult.success
                ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200'
                : 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
            }`}
          >
            {testResult.success
              ? `✅ ${testResult.message ?? 'Microsoft accepted the credentials.'} Token type: ${testResult.tokenType ?? 'Bearer'}, expires in ${testResult.expiresIn ?? '?'}s.`
              : `❌ ${testResult.errorCode ?? testResult.httpStatusCode ?? 'error'}: ${testResult.errorDescription ?? testResult.message ?? 'Microsoft rejected the credentials.'}`}
          </div>
        )}
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-medium">Client secret</h2>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          The secret value is written directly to Azure Key Vault using the API's
          managed identity. The database only stores the Key Vault reference URI —
          never the secret value. Pasting a new secret here replaces the active one.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <input
            type="password"
            autoComplete="new-password"
            className="flex-1 rounded border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-900"
            placeholder="Paste new client secret value"
            value={secretInput}
            onChange={(e) => setSecretInput(e.target.value)}
          />
          <button
            type="button"
            disabled={!secretInput || setSecretMutation.isPending}
            onClick={() => setSecretMutation.mutate()}
            className="rounded bg-primary-700 px-4 py-2 text-white hover:bg-primary-800 disabled:opacity-50"
          >
            {setSecretMutation.isPending ? 'Storing…' : 'Store secret'}
          </button>
          <button
            type="button"
            disabled={!(status?.clientSecretConfigured) || clearSecretMutation.isPending}
            onClick={() => clearSecretMutation.mutate()}
            className="rounded border border-red-300 px-4 py-2 text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
          >
            Clear stored URI
          </button>
        </div>
        {secretMessage && (
          <p
            className={`mt-3 text-sm ${secretMessage.kind === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}
          >
            {secretMessage.text}
          </p>
        )}
      </section>
    </div>
  );
}

function StatusItem({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-2 text-sm dark:border-gray-700 dark:bg-gray-900">
      <span>{label}</span>
      <span className={ok ? 'text-green-700 dark:text-green-300' : 'text-gray-500'}>
        {ok ? '✓ Yes' : '— No'}
      </span>
    </div>
  );
}

function LabeledField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium">
        {label}
        {required && <span className="ml-1 text-red-600">*</span>}
      </span>
      {children}
    </label>
  );
}

function ScriptSetupGuide({
  expanded,
  onToggle,
  setupInfo,
}: {
  expanded: boolean;
  onToggle: () => void;
  setupInfo: TeamsSetupInfoDto | undefined;
}) {
  const scriptUrl = setupInfo?.scriptDownloadUrl ?? apiClient.getPlatformTeamsSetupScriptUrl();
  const absoluteScriptUrl = scriptUrl.startsWith('http')
    ? scriptUrl
    : apiClient.getPlatformTeamsSetupScriptUrl();
  const redirectUri = setupInfo?.redirectUri ?? '(loading…)';
  const apiBaseUrl = setupInfo?.apiBaseUrl ?? '(loading…)';
  const envLabel = setupInfo?.environment ?? 'dev';

  return (
    <section className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
          <span aria-hidden>📘</span>
          First-time setup — run the script, paste the values
        </span>
        <span className="text-blue-700 dark:text-blue-300" aria-hidden>
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-4 text-sm text-blue-900 dark:text-blue-100">
          <p>
            Setting up the central Cloudium Enterprise App takes about{' '}
            <strong>three minutes</strong>. The Azure infrastructure (Key Vault, Bot
            Service, App Service) is already provisioned by the platform deploy. The
            one remaining task — creating the Microsoft Entra ID app registration —
            cannot be expressed in Bicep, so we ship a PowerShell script that does it
            for you. You'll only need to do this once per environment.
          </p>

          <ol className="ml-4 list-decimal space-y-3">
            <li>
              <strong>Download the setup script.</strong> The script is pre-configured
              for the <code>{envLabel}</code> environment with the right redirect URI
              (<code>{redirectUri}</code>) already baked in.
              <div className="mt-2">
                <a
                  href={absoluteScriptUrl}
                  download="Setup-TeamsAppRegistration.ps1"
                  className="inline-flex items-center gap-2 rounded bg-primary-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-800 no-underline"
                >
                  <span aria-hidden>⬇️</span>
                  Download Setup-TeamsAppRegistration.ps1
                </a>
              </div>
            </li>
            <li>
              <strong>Run the script in PowerShell.</strong> Open a PowerShell 7
              terminal in the folder where you saved the file:
              <pre className="mt-2 overflow-x-auto rounded bg-blue-100 p-2 font-mono text-xs dark:bg-blue-950/60">
                {'.\\Setup-TeamsAppRegistration.ps1'}
              </pre>
              A browser window will open for sign-in. Use a Cloudium operator
              account with permission to create app registrations (Application
              Administrator or Global Administrator in the operator tenant).
              The script installs the Microsoft.Graph PowerShell module
              automatically the first time it runs.
            </li>
            <li>
              <strong>Copy the three values the script prints.</strong> When the
              script finishes you'll see a summary block that ends with:
              <pre className="mt-2 overflow-x-auto rounded bg-blue-100 p-2 font-mono text-xs dark:bg-blue-950/60">
                {'Client ID (Application ID) : 00000000-0000-0000-0000-000000000000\nMicrosoft App ID           : 00000000-0000-0000-0000-000000000000   (same value)\nClient Secret              : XXXXXXXXXXXXXXXXXXXXXXXXXXXX\n\nAdmin consent URL (share with each customer tenant admin OR\nopen it yourself if you want to consent inside the operator tenant):\n  https://login.microsoftonline.com/...'}
              </pre>
              Keep the terminal open — the secret is shown only once.
            </li>
            <li>
              <strong>Paste the values into the form below.</strong> The Client ID,
              the Microsoft App ID (same value — checkbox below the field mirrors it
              automatically), and the Client Secret into the bottom section. Click
              <em> Save configuration</em> then <em>Store secret</em>.
            </li>
            <li>
              <strong>Verify with <em>Test credentials</em>.</strong> Microsoft will
              accept the client_credentials round-trip when everything is wired up.
              A green panel means tenants can immediately pick{' '}
              <em>Cloudium Enterprise App</em> in the MSP Portal wizard.
            </li>
          </ol>

          <div className="rounded border border-blue-200 bg-white p-3 text-xs dark:border-blue-800 dark:bg-gray-800">
            <p className="font-semibold">Common gotchas</p>
            <ul className="ml-4 mt-1 list-disc space-y-1">
              <li>
                The first time you run the script PowerShell may prompt to install
                the Microsoft.Graph module — accept the prompt or run{' '}
                <code>Install-Module Microsoft.Graph -Scope CurrentUser</code>
                manually first.
              </li>
              <li>
                You must sign in with an account that has rights to create app
                registrations in the operator tenant. Application Administrator
                is the least-privileged role that works.
              </li>
              <li>
                The redirect URI registered on the app is{' '}
                <code>{redirectUri}</code>. If you change the platform API
                hostname later, re-run the script — it's idempotent and only
                generates a fresh secret on subsequent runs.
              </li>
              <li>
                <code>invalid_client</code> when you click <em>Test credentials</em>
                usually means the secret was truncated when pasted. Re-run the
                script to generate a new one and try again.
              </li>
            </ul>
          </div>

          <div className="rounded border border-blue-200 bg-white p-3 text-xs dark:border-blue-800 dark:bg-gray-800">
            <p className="font-semibold">What the script does</p>
            <ul className="ml-4 mt-1 list-disc space-y-1">
              <li>Creates (or reuses) a multi-tenant Entra ID app registration</li>
              <li>
                Configures Microsoft Graph delegated permissions:{' '}
                <code>User.Read</code>, <code>Team.ReadBasic.All</code>,{' '}
                <code>Channel.ReadBasic.All</code>, <code>ChannelMessage.Send</code>,{' '}
                <code>Chat.ReadWrite</code>, plus standard OpenID Connect scopes
              </li>
              <li>
                Adds <code>{redirectUri}</code> as a web redirect URI
              </li>
              <li>Ensures the service principal exists in the operator tenant</li>
              <li>Generates a 24-month client secret</li>
              <li>Prints the values you need to paste here</li>
            </ul>
            <p className="mt-2">
              The script lives in{' '}
              <code>infrastructure/scripts/Setup-TeamsAppRegistration.ps1</code>{' '}
              in the CloudIncidents repository — you can also download it directly
              from there if you'd rather inspect the source first.
            </p>
          </div>

          <p className="text-xs">
            Full reference (script parameters, manual fallback, rotation runbook):{' '}
            <a
              className="underline font-medium"
              href="https://github.com/Cloudium-AB/CloudIncidents/blob/main/docs/admin-portal/teams-integration-setup.md"
              target="_blank"
              rel="noopener noreferrer"
            >
              docs/admin-portal/teams-integration-setup.md
            </a>
            . Detected API base URL for this environment: <code>{apiBaseUrl}</code>.
          </p>
        </div>
      )}
    </section>
  );
}
